import { IconButton } from "@carbon6/c6-frontend-design";
import { useMemo, type ReactNode } from "react";
import type { TrainingScheduleOperationsItem } from "@/api/trainingSchedule";
import { BulkActionBar } from "@/components/table/BulkActionBar";
import bulkActionBarStyles from "@/components/table/BulkActionBar.module.css";
import { FilterResultCount } from "@/components/filter/FilterResultCount";
import { HatDataTable } from "@/components/table/HatDataTable";
import { StaticTableSource } from "@/components/table/StaticTableSource";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { SCHEDULE_COLUMNS } from "./scheduleColumns";
import { createScheduleRows } from "./ScheduleTableRow";
import { filterSchedulesByStatus, type ScheduleStatusFilter } from "./scheduleFilters";
import type { TrainingSchedulesController } from "./useTrainingSchedules";
import styles from "./SchedulePanel.module.css";

type SchedulePanelProps = {
  controller: TrainingSchedulesController;
  statusFilter: ScheduleStatusFilter;
  // 쓰기 자격(can_manage_schedules). 없으면 편집 버튼을 표시하지 않는다. 열람 전용 목록이다.
  canManage: boolean;
  onEdit: (item: TrainingScheduleOperationsItem) => void;
  // 행 클릭은 실행 이력 창을 연다. 자격과 무관하게 어느 행이든 연다.
  onOpenHistory: (item: TrainingScheduleOperationsItem) => void;
  // 이름 클릭은 Recipe 패널을 연다. 이력 창과 같이 누구나 연다.
  onOpenRecipe: (item: TrainingScheduleOperationsItem) => void;
};

/**
 * Operations > Schedule 목록 표. 전체 응답 위의 정적 source(StaticTableSource)라 페이징도 로딩
 * 깜빡임도 없다. 결과 수와 새로고침은 표 위 `BulkActionBar` 띠가 가진다(rules §17).
 */
export function SchedulePanel({
  controller,
  statusFilter,
  canManage,
  onEdit,
  onOpenHistory,
  onOpenRecipe,
}: SchedulePanelProps): ReactNode {
  const { state, pendingId, refreshing, reload } = controller;

  if (state.status === "error") {
    return (
      <div className={styles.panel}>
        <EmptyState size="lg" tone="error" action={{ label: "다시 시도", onClick: reload }}>
          Schedule 목록을 불러오지 못했습니다. {state.message}
        </EmptyState>
      </div>
    );
  }

  if (state.status !== "success") {
    return (
      <div className={styles.panel}>
        <LoadingState size="lg">Schedule 목록을 불러오고 있습니다.</LoadingState>
      </div>
    );
  }

  return (
    <LoadedPanel
      items={state.data}
      statusFilter={statusFilter}
      canManage={canManage}
      pendingId={pendingId}
      refreshing={refreshing}
      onEdit={onEdit}
      onOpenHistory={onOpenHistory}
      onOpenRecipe={onOpenRecipe}
      onReload={reload}
    />
  );
}

function LoadedPanel({
  items,
  statusFilter,
  canManage,
  pendingId,
  refreshing,
  onEdit,
  onOpenHistory,
  onOpenRecipe,
  onReload,
}: {
  items: readonly TrainingScheduleOperationsItem[];
  statusFilter: ScheduleStatusFilter;
  canManage: boolean;
  pendingId: string | null;
  refreshing: boolean;
  onEdit: (item: TrainingScheduleOperationsItem) => void;
  onOpenHistory: (item: TrainingScheduleOperationsItem) => void;
  onOpenRecipe: (item: TrainingScheduleOperationsItem) => void;
  onReload: () => void;
}): ReactNode {
  const visibleItems = useMemo(
    () => filterSchedulesByStatus(items, statusFilter),
    [items, statusFilter],
  );
  // 행 클릭 = 실행 이력 창, 이름 = Recipe 패널, 이름 칸 버튼 = 편집. 자격과 잠금이 행에 고정되므로
  // 둘이 바뀌면 source 를 다시 만든다.
  const source = useMemo(
    () =>
      new StaticTableSource(
        createScheduleRows(visibleItems, {
          onOpenHistory,
          onOpenRecipe,
          onEdit: canManage ? onEdit : null,
          pendingId,
        }),
      ),
    [visibleItems, canManage, onEdit, onOpenHistory, onOpenRecipe, pendingId],
  );

  // 빈 원인이 다르면 다음 행동도 다르다. 등록이 없는 경우와 필터가 모두 숨긴 경우를 구분한다.
  const emptyMessage =
    items.length === 0
      ? "아직 등록된 Schedule이 없습니다."
      : "조건에 맞는 Schedule이 없습니다. 필터를 초기화해 보세요.";

  return (
    <div className={styles.panel}>
      <div className={bulkActionBarStyles.host}>
        <BulkActionBar
          source={source}
          idleContent={<FilterResultCount count={visibleItems.length} unitLabel="건" />}
          rightContent={
            <IconButton
              text={refreshing ? "새로고침 중…" : "새로고침"}
              size="sm"
              variant="surface"
              disabled={refreshing}
              onClick={onReload}
            />
          }
          ariaLabel="Schedule 목록 도구"
        />
        <HatDataTable
          source={source}
          columns={SCHEDULE_COLUMNS}
          emptyMessage={emptyMessage}
          loadingMessage="불러오고 있습니다."
          ariaLabel="Schedule 목록"
          scrollable
          // 열 최소 폭의 합(번호 열 포함). 이보다 좁으면 표가 가로로 스크롤한다.
          minWidth="1340px"
        />
      </div>
    </div>
  );
}
